import React, { useContext, useEffect, useState } from "react";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import AnticiposExcepciones from "./AnticiposExcepciones";
import CondicionesFinancieras from "./CondicionesFinancieras";
import FurAutonomo from "./FurAutonomo";
import LiquidacionArchivos from "./LiquidacionArchivos";
import LiquidacionGuiada from "./LiquidacionGuiada";
import LiquidacionesRegistradas from "./LiquidacionesRegistradas";
import ProcesamientoSas from "./ProcesamientoSas";
import { pagosApi } from "./pagosApi";
import "./pagos.css";

const PESTANAS = [
  { key: "procesamiento", label: "Procesamiento" },
  { key: "condiciones", label: "Condiciones financieras" },
  { key: "fur-autonomo", label: "FUR autónomo — Simulación" },
  { key: "archivos", label: "Liquidación desde archivos" },
  { key: "anticipos", label: "Anticipos y excepciones" },
];

/**
 * Entrada única de Pagos. "Procesamiento" conserva el flujo de la demo (selección →
 * reconstrucción SQL → controles → registro aislado). "Condiciones financieras" es el catálogo
 * versionado de reglas para pruebas, aislado en ZocoPagos_Dev. "FUR autónomo" simula el FUR con
 * netos del motor y lo compara con el histórico; no paga ni genera archivos bancarios.
 */
export default function Pagos() {
  const { darkMode } = useContext(DarkModeContext);
  const token = getToken();
  const [persistencia, setPersistencia] = useState(null);
  const [registros, setRegistros] = useState(0);
  const [pestana, setPestana] = useState("procesamiento");

  useEffect(() => {
    let vigente = true;
    Promise.resolve().then(() => pagosApi.estadoLiquidaciones(token))
      .then((estado) => { if (vigente) setPersistencia(estado || { habilitada: false }); })
      .catch((error) => {
        if (vigente) setPersistencia({ habilitada: false, motivo: error?.message || "Persistencia no disponible." });
      });
    return () => { vigente = false; };
  }, [token]);

  return (
    <main className={`pagos-page${darkMode ? " pagos-page--dark" : ""}`} data-testid="pagos-page">
      <header className="pagos-header">
        <div>
          <p className="pagos-eyebrow">Pagos a comercios</p>
          <h1>Preparar una liquidación</h1>
          <p className="pagos-subtitle">Un único flujo guiado desde los archivos hasta la revisión del FUR. No genera pagos ni archivos bancarios.</p>
        </div>
        <div className="pagos-header-badges">
          <span className="pagos-mode-badge">Simulación · solo lectura productiva</span>
          <span className="pagos-status pagos-status--warning">Motor autónomo no validado</span>
        </div>
      </header>

      <LiquidacionGuiada token={token} />

      <details className="pagos-advanced-tools">
        <summary>Herramientas avanzadas <small>Sólo para consulta técnica y usuarios autorizados</small></summary>
        <div className="pagos-tabs" role="tablist" aria-label="Herramientas avanzadas de Pagos">
          {PESTANAS.map((item) => (
            <button key={item.key} type="button" role="tab" id={`pagos-tab-${item.key}`} aria-selected={pestana === item.key}
              aria-controls={`pagos-panel-${item.key}`} className={pestana === item.key ? "is-active" : ""} onClick={() => setPestana(item.key)}>
              {item.label}
            </button>
          ))}
        </div>

        {pestana === "procesamiento" ? (
        <div role="tabpanel" id="pagos-panel-procesamiento" aria-labelledby="pagos-tab-procesamiento">
          <div className="pagos-alert pagos-alert--info" role="note">
            <strong>Ruta única para la demo:</strong> seleccioná la fecha y procesá la importación SAS sugerida. El sistema reconstruye el FUR y ejecuta los controles sin solicitar el Reporte Excel.
          </div>

          <ProcesamientoSas token={token} persistencia={persistencia}
            onRegistered={() => setRegistros((value) => value + 1)} />

          <LiquidacionesRegistradas token={token} persistencia={persistencia} refreshKey={registros} />
        </div>
      ) : pestana === "condiciones" ? (
        <div role="tabpanel" id="pagos-panel-condiciones" aria-labelledby="pagos-tab-condiciones">
          <CondicionesFinancieras token={token} />
        </div>
      ) : pestana === "fur-autonomo" ? (
        <div role="tabpanel" id="pagos-panel-fur-autonomo" aria-labelledby="pagos-tab-fur-autonomo">
          <FurAutonomo token={token} />
        </div>
      ) : pestana === "archivos" ? (
        <div role="tabpanel" id="pagos-panel-archivos" aria-labelledby="pagos-tab-archivos">
          <LiquidacionArchivos token={token} />
        </div>
      ) : (
        <div role="tabpanel" id="pagos-panel-anticipos" aria-labelledby="pagos-tab-anticipos">
          <AnticiposExcepciones token={token} />
        </div>
        )}
      </details>
    </main>
  );
}
